import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Badge, Button, Drawer, Tabs, Tooltip } from "antd";
import { BellOutlined, SettingOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { useSocket } from "../contexts/SocketContext";
import { useAuth } from "../contexts/AuthContext";
import { RequestsTab } from "./RequestsTab";
import { SettingsTab } from "./settings/SettingsTab";

const playApprovalSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1100, 0.18]].forEach(([freq, delay]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.22, ctx.currentTime + delay);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.35);
      o.start(ctx.currentTime + delay);
      o.stop(ctx.currentTime + delay + 0.35);
    });
  } catch {}
};

export function ApprovalCenter() {
  const { authState } = useAuth();
  const { socket } = useSocket();
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);

  const [pendingCount, setPendingCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("requests");
  const [users, setUsers] = useState([]);

  const isAdmin = authState?.user?.admin;
  const userId = authState?.id;

  // userMap for name lookups in RequestsTab
  const userMap = useMemo(() => {
    const m = {};
    users.forEach(u => { m[u.id] = u.name || u.username; });
    return m;
  }, [users]);

  // Fetch initial pending count + users
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API_DOMAIN}/approval-requests?status=pending`, head)
      .then(r => {
        const list = r.data?.data ?? r.data ?? [];
        setPendingCount(Array.isArray(list) ? list.length : 0);
      })
      .catch(() => {});
    axios.get(`${API_DOMAIN}/auth/get_all_users`, { params: { api_key: "uzdpart" }, ...head })
      .then(r => setUsers(r.data?.data ?? r.data ?? []))
      .catch(() => {});
  }, [userId]); // eslint-disable-line

  // Socket: live count updates + sound
  useEffect(() => {
    if (!socket) return;

    const onCreated = () => {
      setPendingCount(prev => prev + 1);
      playApprovalSound();
    };

    const onUpdated = (data) => {
      const newStatus = data?.data?.status;
      if (newStatus && newStatus !== "pending") {
        setPendingCount(prev => Math.max(0, prev - 1));
      }
    };

    socket.on("approval_request:created", onCreated);
    socket.on("approval_request:updated", onUpdated);
    return () => {
      socket.off("approval_request:created", onCreated);
      socket.off("approval_request:updated", onUpdated);
    };
  }, [socket]);

  const handleRequestDecided = useCallback((id, newStatus) => {
    if (newStatus !== "pending") setPendingCount(prev => Math.max(0, prev - 1));
  }, []);

  const tabItems = [
    {
      key: "requests",
      label: (
        <span>
          Requests
          {pendingCount > 0 && (
            <Badge count={pendingCount} size="small" color="#faad14" style={{ marginLeft: 6 }} />
          )}
        </span>
      ),
      children: (
        <RequestsTab
          open={drawerOpen && activeTab === "requests"}
          userMap={userMap}
          authState={authState}
          onRequestDecided={handleRequestDecided}
        />
      ),
    },
    ...(isAdmin ? [{
      key: "settings",
      label: (
        <span>
          <SettingOutlined style={{ marginRight: 4 }} />
          Settings
        </span>
      ),
      children: <SettingsTab users={users} />,
    }] : []),
  ];

  return (
    <>
      <Tooltip title="Approval Requests" placement="bottom">
        <Badge count={pendingCount} size="small" offset={[-2, 2]}>
          <Button
            type="text"
            icon={<BellOutlined style={{ fontSize: 20, color: pendingCount > 0 ? "#faad14" : "white" }} />}
            onClick={() => setDrawerOpen(true)}
            style={{ padding: "4px 6px", display: "flex", alignItems: "center" }}
          />
        </Badge>
      </Tooltip>

      <Drawer
        title="Approval Center"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        styles={{ body: { padding: "0 16px 16px", display: "flex", flexDirection: "column" } }}
        destroyOnHidden={false}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
          tabBarStyle={{ flexShrink: 0 }}
          items={tabItems}
        />
      </Drawer>
    </>
  );
}
